import React from 'react';
import {
  LS_KEY_EXTENSION_PAUSED_DISMISSED,
  LS_KEY_EXTENSION_SEEN,
} from 'src/constants/localStorageKeys';
import apiClient from './apiClient';

/**
 * Handoff to the Abekus hiring Chrome extension.
 *
 * When a candidate is redirected out to an employer's application form, the
 * extension has no way to know which Abekus job that form belongs to — the new
 * tab is on a third-party host with no shared state. So we announce the job
 * here, and the extension's content script (which runs only on Abekus origins)
 * forwards it to its service worker, keyed by apply URL.
 *
 * Delivered by `window.postMessage` targeted at our own origin: the extension
 * listens in the same window, and no cross-origin listener can see it. It is a
 * no-op when the extension isn't installed — nothing listens, nothing breaks.
 */
const MESSAGE_SOURCE = 'abekus-jobs';
const MESSAGE_TYPE = 'ABEKUS_EXTERNAL_APPLY';

export interface ExternalApplyHandoff {
  jobId: string;
  applyUrl: string;
  company?: string | null;
  title?: string | null;
}

export const announceExternalApply = ({
  jobId,
  applyUrl,
  company,
  title,
}: ExternalApplyHandoff): void => {
  if (!jobId || !applyUrl) return;
  try {
    window.postMessage(
      {
        source: MESSAGE_SOURCE,
        type: MESSAGE_TYPE,
        jobId,
        applyUrl,
        company: company ?? null,
        title: title ?? null,
      },
      window.location.origin,
    );
  } catch {
    // postMessage can throw on an origin mismatch in exotic embedding setups;
    // the handoff is an enhancement, so failing it must not block the redirect.
  }
};

/** Where "Add to Chrome" sends a candidate: the extension's Web Store listing. */
export const EXTENSION_STORE_URL =
  'https://chromewebstore.google.com/detail/abekus-autofill-%E2%80%93-instant/ihcnohidjeaidjppdnhbbjmhmhjofpgg';

/** The attribute the extension's content script stamps on `<html>`. */
const INSTALLED_ATTR = 'data-abekus-extension';

/**
 * Whether the extension is running on this page. Its content script loads at
 * `document_idle`, which can land after the job page renders, so callers that
 * render on it should use {@link useIsExtensionInstalled} to catch the stamp
 * arriving late.
 */
export const isExtensionInstalled = (): boolean =>
  document.documentElement.hasAttribute(INSTALLED_ATTR);

/**
 * The store only installs into Chromium browsers (Chrome, Edge, Brave, Arc…),
 * so there is nothing to offer anyone else. `userAgentData` is Chromium-only;
 * its absence is the signal.
 */
export const canInstallExtension = (): boolean =>
  typeof navigator !== 'undefined' && 'userAgentData' in navigator;

export const useIsExtensionInstalled = (): boolean => {
  const [installed, setInstalled] = React.useState(isExtensionInstalled);

  React.useEffect(() => {
    if (installed) return undefined;
    const observer = new MutationObserver(() => {
      if (isExtensionInstalled()) {
        setInstalled(true);
        observer.disconnect();
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [INSTALLED_ATTR],
    });
    return () => observer.disconnect();
  }, [installed]);

  return installed;
};

/**
 * The first version that asks Chrome for access to every site. Chrome turns an
 * installed extension off when an update asks for more than it had, until the
 * candidate accepts — so a browser that ran an older one and now shows none
 * after this is out has, almost always, been paused by Chrome.
 */
export const EXTENSION_PERMISSION_VERSION = '0.4.0';

/**
 * The first version that sets the page Chrome opens on uninstall, which is
 * what forgets a sighting. One from an older version could outlive the
 * extension itself, so it is never trusted.
 */
const UNINSTALL_PAGE_VERSION = '0.3.1';

// Older than this, a sighting is not trusted to mean the extension is still in
// this browser's toolbar.
const SEEN_FRESH_MS = 30 * 24 * 60 * 60 * 1000;
// The content script stamps the page at document_idle; give it this long.
const STAMP_GRACE_MS = 4000;

interface ExtensionSighting {
  version: string;
  at: number;
}

/** -1, 0 or 1, comparing dotted versions numerically ("0.10.0" > "0.9.1"). */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => Number(n) || 0);
  const pb = b.split('.').map((n) => Number(n) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d) return d > 0 ? 1 : -1;
  }
  return 0;
};

/** This browser's last sighting, when it can still be trusted: recent, and
 *  from a version whose uninstall would have forgotten it. */
const readSighting = (): ExtensionSighting | null => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(LS_KEY_EXTENSION_SEEN) || 'null',
    );
    if (
      raw &&
      typeof raw.version === 'string' &&
      typeof raw.at === 'number' &&
      Date.now() - raw.at <= SEEN_FRESH_MS &&
      compareVersions(raw.version, UNINSTALL_PAGE_VERSION) >= 0
    ) {
      return raw;
    }
  } catch {
    // Unreadable or blocked storage: as if never seen.
  }
  return null;
};

/**
 * Whether this browser ran the extension in the last 30 days and it was not
 * uninstalled since. Someone it is true for already has it: never offer them
 * the install.
 */
export const hadExtensionRecently = (): boolean => Boolean(readSighting());

/** Note the version on this page, when the extension stamped one. */
const recordSighting = () => {
  const version = document.documentElement.getAttribute(INSTALLED_ATTR);
  if (!version) return;
  try {
    localStorage.setItem(
      LS_KEY_EXTENSION_SEEN,
      JSON.stringify({ version, at: Date.now() }),
    );
    // Back and running: a later pause deserves its own notice.
    localStorage.removeItem(LS_KEY_EXTENSION_PAUSED_DISMISSED);
  } catch {
    // Storage blocked: nothing to remember with, so nothing will be claimed.
  }
};

/**
 * Remember, in this browser only, that the extension runs here. Called once
 * from the app root, so any Abekus page the candidate opens counts.
 */
export const rememberExtension = (): (() => void) => {
  recordSighting();
  const observer = new MutationObserver(recordSighting);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [INSTALLED_ATTR],
  });
  return () => observer.disconnect();
};

/** The extension was removed from this browser: never say it was paused. */
export const forgetExtension = () => {
  try {
    localStorage.removeItem(LS_KEY_EXTENSION_SEEN);
    localStorage.removeItem(LS_KEY_EXTENSION_PAUSED_DISMISSED);
  } catch {
    // Nothing stored to forget.
  }
};

export const dismissExtensionPaused = () => {
  try {
    localStorage.setItem(LS_KEY_EXTENSION_PAUSED_DISMISSED, '1');
  } catch {
    // Blocked storage: it hides for this page view only.
  }
};

let publishedVersion: Promise<string | null> | null = null;

/** The version the Web Store hands out now, asked once per page load. */
const getPublishedVersion = () => {
  publishedVersion =
    publishedVersion ??
    apiClient
      .getPublishedExtensionVersion()
      .then((res) => res.published_version ?? null)
      .catch(() => null);
  return publishedVersion;
};

/**
 * Whether Chrome has, as far as this browser can tell for sure, paused the
 * extension after an update. Every one of these must hold:
 * - this browser ran the extension within 30 days, on a version that sets
 *   the uninstall page (so an uninstall would have forgotten it) and from
 *   before the all-sites permission;
 * - the version now on the Web Store asks for that permission;
 * - this page carries no stamp from it, even after the content script had
 *   time to load;
 * - the candidate has not closed the notice.
 */
export const useIsExtensionPaused = (): boolean => {
  const installed = useIsExtensionInstalled();
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    // Running here: the return below already says not paused.
    if (installed) return undefined;
    const seen = readSighting();
    let dismissed = false;
    try {
      dismissed =
        localStorage.getItem(LS_KEY_EXTENSION_PAUSED_DISMISSED) === '1';
    } catch {
      dismissed = false;
    }
    if (
      !canInstallExtension() ||
      dismissed ||
      !seen ||
      compareVersions(seen.version, EXTENSION_PERMISSION_VERSION) >= 0
    ) {
      return undefined;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      getPublishedVersion().then((published) => {
        if (!live || isExtensionInstalled() || !published) return;
        setPaused(
          compareVersions(published, EXTENSION_PERMISSION_VERSION) >= 0,
        );
      });
    }, STAMP_GRACE_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [installed]);

  return paused && !installed;
};
